import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { GoonAPIClient, Post, UserProfile } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import { consume } from '@lit/context';
import { authContext, AuthState } from '../context/AuthContext';

@customElement('creator-page')
export class CreatorPage extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) creatorSlug = '';

  @state() creator: UserProfile | null = null;
  @state() pinnedPosts: Post[] = [];
  @state() posts: Post[] = [];
  @state() isFollowing = false;
  @state() loading = true;

  @consume({ context: authContext, subscribe: true })
  auth!: AuthState;

  async firstUpdated() {
    const client = new GoonAPIClient(API_ENDPOINT);
    try {
      this.creator = await client.getUserProfileBySlug(this.creatorSlug);
      this.pinnedPosts = await client.getUserProfilePinnedPosts(this.creatorSlug);
      this.posts = await client.getUserProfilePosts(this.creatorSlug);

      try {
        const following = await client.getFollowingProfiles();
        this.isFollowing = following.following.some(p => p.id === this.creatorSlug);
      } catch (err) {
        console.error(err)
      }
    } catch (err) {
      console.error('Failed to load creator page', err);
    } finally {
      this.loading = false;
    }
  }

  async handleFollowToggle() {
    if (!this.auth.userId) {
      this.dispatchEvent(new CustomEvent('open-auth-modal', { bubbles: true, composed: true }));
      return;
    }
    const client = new GoonAPIClient(API_ENDPOINT);
    if (this.isFollowing) {
      await client.unfollowUser(this.creatorSlug);
      this.isFollowing = false;
    } else {
      await client.followUser(this.creatorSlug);
      this.isFollowing = true;
    }
  }

  render() {
    if (this.loading || !this.creator) return html`<div class="p-4">Loading...</div>`;

    return html`
      <!-- Header -->
      <auth-provider>
        <app-header></app-header>
      </auth-provider>

      <div class="flex flex-col lg:flex-row gap-6 px-4 sm:px-6 py-6 mx-auto w-full">
        <div class="w-full lg:w-4/6">
          <div class="card bg-base-300 p-4 mb-6">
            <div class="flex gap-4 items-center">
              <img src="${this.creator.avatar_url || '/static/images/default-avatar.webp'}" class="w-20 h-20 rounded-full object-cover ring ring-primary ring-offset-base-100 ring-offset-2" />
              <div class="flex-1">
                <h2 class="text-2xl font-bold">${this.creator.display_name}</h2>
                <p class="text-base-content/70">${this.creator.bio}</p>
              </div>
              <div class="flex gap-2">
                <button class="btn btn-sm btn-primary" @click=${this.handleFollowToggle}>
                  ${this.isFollowing ? 'Unfollow' : 'Follow'}
                </button>
                <button class="btn btn-sm btn-outline">DM</button>
              </div>
            </div>
          </div>

          <auth-provider>
            ${this.pinnedPosts.length ? html`
              <div class="card bg-base-300 p-4 mb-6">
                <h3 class="text-lg font-bold mb-2">Pinned Posts</h3>
                <div class="flex flex-col gap-4">
                  ${this.pinnedPosts.map(post => html`<post-card .post=${post} .creator=${this.creator}></post-card>`)}
                </div>
              </div>` : null}

            <div class="flex flex-col gap-4">
              ${this.posts.map(post => html`<post-card .post=${post} .creator=${this.creator}></post-card>`)}
            </div>
          <auth-provider>
        </div>

        <aside class="w-full lg:w-2/6">
          <div class="sticky top-20">
            <realtime-chat .channelSlug=${'creator-' + this.creatorSlug}></realtime-chat>
          </div>
        </aside>
      </div>

      <auth-modal></auth-modal>
    `;
  }
}

